import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Chart from '../../utils/chart';

const COLORS = ['#007bff', '#ff4169', '#17c671', '#8445f7', '#fb7906'];

const hexToRgb = (hex) => {
  const normalized = (hex || '#000000').replace('#', '');
  const expanded = normalized.length === 3
    ? normalized.split('').map(character => character + character).join('')
    : normalized;
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(expanded);

  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : { r: 0, g: 0, b: 0 };
};

const defaultLineOptions = {
  responsive: true,
  legend: {
    position: 'top',
  },
  elements: {
    line: {
      tension: 0.3,
    },
    point: {
      radius: 0,
    },
  },
  scales: {
    xAxes: [
      {
        gridLines: false,
        ticks: {
          callback(tick, index) {
            return index % 2 !== 0 ? '' : tick;
          },
        },
      },
    ],
    yAxes: [
      {
        ticks: {
          suggestedMax: 45,
          callback(tick) {
            if (tick === 0) return tick;
            return tick > 999 ? `${(tick / 1000).toFixed(1)}K` : tick;
          },
        },
      },
    ],
  },
  hover: {
    mode: 'nearest',
    intersect: false,
  },
  tooltips: {
    custom: false,
    mode: 'index',
    intersect: false,
  },
};

const defaultPieOptions = {
  responsive: true,
  legend: {
    position: 'right',
    labels: {
      padding: 7,
      boxWidth: 20,
    },
  },
  cutoutPercentage: 0,
  tooltips: {
    custom: false,
    mode: 'index',
    position: 'nearest',
  },
};

const VChart = ({
  chartOptions,
  className,
  datasets,
  height,
  labels,
  style,
  type,
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current || !datasets || datasets.length === 0) {
      return undefined;
    }

    let normalizedDatasets;

    if (type === 'pie') {
      normalizedDatasets = datasets.map(dataset => ({
        ...dataset,
        borderWidth: dataset.borderWidth === undefined ? 0.5 : dataset.borderWidth,
      }));
    } else {
      normalizedDatasets = datasets.map((dataset, index) => {
        const color = hexToRgb(dataset.color || COLORS[index % COLORS.length]);
        return {
          ...dataset,
          fill: dataset.fill === undefined ? false : dataset.fill,
          backgroundColor: dataset.backgroundColor
            || `rgba(${color.r},${color.g},${color.b},0.1)`,
          borderColor: dataset.borderColor
            || `rgba(${color.r},${color.g},${color.b},1)`,
          pointBackgroundColor: dataset.pointBackgroundColor || 'rgba(255,255,255,1)',
          pointHoverBackgroundColor: dataset.pointHoverBackgroundColor
            || `rgba(${color.r},${color.g},${color.b},1)`,
          borderWidth: dataset.borderWidth === undefined ? 1.5 : dataset.borderWidth,
          pointRadius: dataset.pointRadius === undefined ? 0 : dataset.pointRadius,
          pointHoverRadius: dataset.pointHoverRadius === undefined ? 3 : dataset.pointHoverRadius,
          borderDash: dataset.borderDash || [0, 0],
        };
      });
    }

    const hasData = normalizedDatasets.some(
      dataset => Array.isArray(dataset.data) && dataset.data.length > 0,
    );
    if (!hasData) return undefined;

    chartRef.current = new Chart(canvasRef.current, {
      type: type || 'LineWithLine',
      data: {
        labels: labels || [],
        datasets: normalizedDatasets,
      },
      options: {
        ...(type === 'pie' ? defaultPieOptions : defaultLineOptions),
        ...(chartOptions || {}),
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartOptions, datasets, labels, type]);

  return (
    <canvas
      height={height}
      ref={canvasRef}
      style={{ ...(style || {}), maxWidth: '100%' }}
      className={className || undefined}
    />
  );
};

VChart.propTypes = {
  labels: PropTypes.array,
  datasets: PropTypes.array,
  chartOptions: PropTypes.object,
  type: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  className: PropTypes.string,
};

VChart.defaultProps = {
  labels: [],
  datasets: [],
  chartOptions: {},
  type: 'line',
  height: '100',
  style: {},
  className: undefined,
};

export default VChart;
